/* MetaPanel.js */

import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import { RenderContext } from "../../../Enums.js";
import { renderField, makeRenderRequest } from "../../../fields/Fields.js";
import "./MetaPanel.css";

function getNestedPropertyValue(object, path) {
  if (!object || typeof object !== "object" || !path || typeof path !== "string") return undefined;
  return path.split(".").reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), object);
}

export default function MetaPanel({ fields, row, renderEnv }) {
  // visible fields as before
  const visibleFields = Array.isArray(fields)
    ? fields.filter((f) => f.datagridOnly !== true && (f.metaPanel === true || f.metaPanel !== undefined))
    : [];

  // pull out an ID field (case-insensitive match on field or headerName)
  const { idField, otherFields } = useMemo(() => {
    let idIdx = -1;
    for (let i = 0; i < visibleFields.length; i++) {
      const f = visibleFields[i];
      const name = (f.field || f.headerName || "").toString().toLowerCase();
      if (name === "id") { idIdx = i; break; }
    }
    const copy = [...visibleFields];
    const idField = idIdx >= 0 ? copy.splice(idIdx, 1)[0] : null;
    return { idField, otherFields: copy };
  }, [visibleFields]);

  // split remaining fields into two balanced columns (don’t hardcode counts)
  const [leftCol, rightCol] = useMemo(() => {
    const n = otherFields.length;
    const mid = Math.ceil(n / 2);
    return [otherFields.slice(0, mid), otherFields.slice(mid)];
  }, [otherFields]);

  return (
    // keep mp-debug while you verify gutters; remove it afterwards
    <div className="mp-root">
      {idField && <IdRow field={idField} row={row} renderEnv={renderEnv} />}

      <div className="mp-two-col">
        <div className="mp-col">
          {leftCol.map((f) => (
            <MetaRow key={f.field || f.headerName} field={f} row={row} renderEnv={renderEnv} />
          ))}
        </div>
        <div className="mp-col">
          {rightCol.map((f) => (
            <MetaRow key={f.field || f.headerName} field={f} row={row} renderEnv={renderEnv} />
          ))}
        </div>
      </div>
    </div>
  );
}

function IdRow({ field, row, renderEnv }) {
  const value = getNestedPropertyValue(row, field.dataSource);

  const req = makeRenderRequest({
    field,
    row,
    value,
    context: RenderContext.META_PANEL,
    theme: renderEnv?.theme,
    projectCode: renderEnv?.projectCode,
    locale: renderEnv?.locale,
    onCellUpdate: renderEnv?.handlers?.onCellUpdate,
    onFileUpdate: renderEnv?.handlers?.onFileUpdate,
    onReviewerChange: renderEnv?.handlers?.onReviewerChange,
  });

  return (
    <div className="mp-id-row">
      <Typography className="meta-panel-label mp-id-label">{field.headerName}</Typography>
      <div className="mp-id-value">{renderField(req)}</div>
    </div>
  );
}

function MetaRow({ field, row, renderEnv }) {
  const value = getNestedPropertyValue(row, field.dataSource);

  const req = makeRenderRequest({
    field,
    row,
    value,
    context: RenderContext.META_PANEL,
    theme: renderEnv?.theme,
    projectCode: renderEnv?.projectCode,
    locale: renderEnv?.locale,
    onCellUpdate: renderEnv?.handlers?.onCellUpdate,
    onFileUpdate: renderEnv?.handlers?.onFileUpdate,
    onReviewerChange: renderEnv?.handlers?.onReviewerChange,
  });

  return (
    <div className="mp-row">
      <Typography className="meta-panel-label">{field.headerName}</Typography>
      <div className="mp-value">{renderField(req)}</div>
    </div>
  );
}
